import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { FileText, DollarSign, Eye, Download, Trash2, CheckSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { InvoiceCreator } from '@/components/admin/InvoiceCreator';
import { useAuth } from '@/hooks/useAuth';

interface POManagementProps {
  pos: any[];
  onUpdate: () => void;
}

export function POManagement({ pos, onUpdate }: POManagementProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [selectedPO, setSelectedPO] = useState<any>(null);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [selectedPOs, setSelectedPOs] = useState<Set<string>>(new Set());
  const [isClearing, setIsClearing] = useState(false);

  const isMattMonjan = user?.email === 'matt.monjan@infusedu.com' || user?.email === 'matt.monjan@infusesdu.com';

  const handleCreateInvoice = (po: any) => {
    setSelectedPO(po);
    setShowInvoiceDialog(true);
  };

  const handleInvoiceCreated = () => {
    setShowInvoiceDialog(false);
    setSelectedPO(null);
    onUpdate();
    toast({
      title: "Success",
      description: "Invoice created successfully",
    });
  };

  const handleSelectPO = (poId: string, checked: boolean) => {
    const newSelected = new Set(selectedPOs);
    if (checked) {
      newSelected.add(poId);
    } else {
      newSelected.delete(poId);
    }
    setSelectedPOs(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedPOs(new Set(pos.map(po => po.id)));
    } else {
      setSelectedPOs(new Set());
    }
  };

  const handleClearSelected = async () => {
    if (selectedPOs.size === 0) return;
    
    setIsClearing(true);
    try {
      const { error } = await supabase
        .from('purchase_orders')
        .delete()
        .in('id', Array.from(selectedPOs));

      if (error) throw error;

      toast({
        title: "Success",
        description: `${selectedPOs.size} purchase order(s) cleared successfully`,
      });
      setSelectedPOs(new Set());
      onUpdate();
    } catch (error) {
      console.error('Error clearing selected POs:', error);
      toast({
        title: "Error",
        description: "Failed to clear selected purchase orders",
        variant: "destructive",
      });
    } finally {
      setIsClearing(false);
    }
  };

  const handleClearAll = async () => {
    setIsClearing(true);
    try {
      const { error } = await supabase
        .from('purchase_orders')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all by using a condition that matches all

      if (error) throw error;

      toast({
        title: "Success",
        description: "All purchase orders cleared successfully",
      });
      setSelectedPOs(new Set());
      onUpdate();
    } catch (error) {
      console.error('Error clearing all POs:', error);
      toast({
        title: "Error",
        description: "Failed to clear all purchase orders",
        variant: "destructive",
      });
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <div className="space-y-6">
      {isMattMonjan && pos.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Clear Purchase Orders (Matt Monjan Only)
            </CardTitle>
            <CardDescription>
              Select individual POs or clear all purchase orders from the system
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="select-all"
                checked={selectedPOs.size === pos.length && pos.length > 0}
                onCheckedChange={handleSelectAll}
              />
              <label htmlFor="select-all" className="text-sm font-medium">
                Select All ({pos.length})
              </label>
            </div>
            
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline" 
                  disabled={selectedPOs.size === 0 || isClearing}
                  className="text-destructive hover:text-destructive"
                >
                  <CheckSquare className="h-4 w-4 mr-2" />
                  Clear Selected ({selectedPOs.size})
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear Selected Purchase Orders</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to permanently delete {selectedPOs.size} selected purchase order(s)? 
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleClearSelected}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Clear Selected
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="destructive" 
                  disabled={isClearing}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear All POs
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear All Purchase Orders</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to permanently delete ALL {pos.length} purchase orders? 
                    This action cannot be undone and will remove all purchase order data from the system.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleClearAll}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Clear All
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      )}
      {pos.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Purchase Orders</h3>
              <p className="text-muted-foreground">
                No purchase orders have been uploaded by districts yet.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {pos.map((po) => (
            <Card key={po.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      {isMattMonjan && (
                        <Checkbox
                          checked={selectedPOs.has(po.id)}
                          onCheckedChange={(checked) => handleSelectPO(po.id, checked as boolean)}
                        />
                      )}
                      <CardTitle>PO #{po.po_number}</CardTitle>
                      <Badge variant={po.invoice_created ? "default" : "secondary"}>
                        {po.invoice_created ? "Invoice Created" : "Pending"}
                      </Badge>
                    </div>
                    <CardDescription>
                      {po.bill_to_name} • {new Date(po.po_date).toLocaleDateString()} • ${po.total_amount.toFixed(2)}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {po.po_file_url && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={po.po_file_url} target="_blank" rel="noopener noreferrer">
                          <Eye className="h-4 w-4 mr-2" />
                          View File
                        </a>
                      </Button>
                    )}
                    {!po.invoice_created && (
                      <Button 
                        size="sm" 
                        onClick={() => handleCreateInvoice(po)}
                      >
                        <DollarSign className="h-4 w-4 mr-2" />
                        Create Invoice
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-semibold mb-2">Billing Information</h4>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>{po.bill_to_name}</p>
                      <p>{po.bill_to_address}</p>
                      <p>{po.bill_to_city}, {po.bill_to_state} {po.bill_to_zip}</p>
                      {po.tax_exemption_number && (
                        <p>Tax Exempt: {po.tax_exemption_number}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Financial Summary</h4>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Subtotal:</span>
                        <span>${po.subtotal.toFixed(2)}</span>
                      </div>
                      {po.shipping_cost > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Shipping:</span>
                          <span>${po.shipping_cost.toFixed(2)}</span>
                        </div>
                      )}
                      {po.tax_amount > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Tax:</span>
                          <span>${po.tax_amount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold border-t pt-1">
                        <span>Total:</span>
                        <span>${po.total_amount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Additional Details</h4>
                    <div className="text-sm text-muted-foreground space-y-1">
                      {po.requested_by && <p>Requested by: {po.requested_by}</p>}
                      {po.department && <p>Department: {po.department}</p>}
                      {po.account_code && <p>Account: {po.account_code}</p>}
                      {po.approved_by && <p>Approved by: {po.approved_by}</p>}
                      <p>Terms: {po.terms}</p>
                    </div>
                  </div>
                </div>

                {po.po_line_items && po.po_line_items.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-semibold mb-3">Line Items</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-muted px-4 py-2 grid grid-cols-5 gap-4 text-sm font-medium">
                        <span>Qty</span>
                        <span>Item #</span>
                        <span>Description</span>
                        <span>Unit Price</span>
                        <span>Total</span>
                      </div>
                      {po.po_line_items.map((item: any, index: number) => (
                        <div key={index} className="px-4 py-2 grid grid-cols-5 gap-4 text-sm border-t">
                          <span>{item.quantity}</span>
                          <span className="text-muted-foreground">{item.item_number || '-'}</span>
                          <span>{item.description}</span>
                          <span>${item.unit_price.toFixed(2)}</span>
                          <span>${item.total_price.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {po.comments && (
                  <div className="mt-4">
                    <h4 className="font-semibold mb-2">Comments</h4>
                    <p className="text-sm text-muted-foreground">{po.comments}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
          </DialogHeader>
          {selectedPO && (
            <InvoiceCreator po={selectedPO} onSuccess={handleInvoiceCreated} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}